import { useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';

export type PublicCustomColors = {
  /** Background color for the public share page surface. */
  pageBgColor: string | undefined;
  /** Background color for the public workspace header. */
  headerBgColor: string | undefined;
  /** Readable text/icon color derived from `headerBgColor` (undefined when the header color is unset). */
  headerForegroundColor: string | undefined;
  /** Background color for branded primary buttons (e.g. "Save all"). */
  buttonBgColor: string | undefined;
  /** Text color for the public header title/headline. */
  headlineColor: string | undefined;
};

/**
 * The share link's custom brand colors, when the publisher set them, resolved to hex values plus
 * the derived foreground colors consumers need (the backend stores no foreground colors). Each value
 * is `undefined` when unset so consumers fall through to their default Tailwind classes. Must be
 * called within a `ShortIdProvider`.
 */
export const usePublicCustomColors = (): PublicCustomColors => {
  const { presentationSettings } = useShortIdContext();
  const customColors = presentationSettings?.customColors;

  return useMemo(() => {
    const headerBgColor = customColors?.headerBgColor?.hex;

    return {
      pageBgColor: customColors?.pageBgColor?.hex,
      headerBgColor,
      headerForegroundColor: headerBgColor ? getContrastColor(headerBgColor) : undefined,
      buttonBgColor: customColors?.buttonBgColor?.hex,
      headlineColor: customColors?.headlineBgColor?.hex,
    };
  }, [customColors]);
};
